import { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { employeeService } from '../../../services/employeeService';
import { extractList, extractMeta } from '../../../services/api';
import { parsePeriod, CURRENT_PERIOD } from '../../../constants';
import Loading from '../../../shared/Loading';
import EmptyState from '../../../shared/EmptyState';
import Badge from '../../../shared/Badge';
import InputModal from '../components/InputModal';

const ReportDetailPage = () => {
  const { uniformReportId } = useParams();

  const [report, setReport] = useState(null);
  const [details, setDetails] = useState([]);
  const [detailsMeta, setDetailsMeta] = useState({ page: 1, last_page: 1, total: 0 });
  const [detailsPage, setDetailsPage] = useState(1);
  const [uniforms, setUniforms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState('');
  const [previewImg, setPreviewImg] = useState(null);
  const [editingDetail, setEditingDetail] = useState(null);

  // Edit report notes
  const [editingNotes, setEditingNotes] = useState(false);
  const [notesText, setNotesText] = useState('');
  const [savingNotes, setSavingNotes] = useState(false);

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(''), 3000);
  };

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [rRes, dRes, uRes] = await Promise.all([
        employeeService.getReportById(uniformReportId),
        employeeService.getReportDetails(uniformReportId, { page: detailsPage }),
        employeeService.getUniforms(),
      ]);
      const rep = rRes.data;
      setReport(rep);
      setNotesText(rep?.notes || '');
      setDetails(extractList(dRes.data));
      setDetailsMeta(extractMeta(dRes.data));
      setUniforms(Array.isArray(uRes.data) ? uRes.data : []);
    } catch (err) {
      showToast(err?.message || 'Gagal memuat laporan');
    } finally {
      setLoading(false);
    }
  }, [uniformReportId, detailsPage]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleDelete = async (detailId) => {
    if (!window.confirm('Hapus item ini?')) return;
    try {
      await employeeService.deleteReportDetail(detailId);
      showToast('Data berhasil dihapus');
      fetchAll();
    } catch (err) {
      showToast(err?.message || 'Gagal menghapus data');
    }
  };

  const handleEditDetail = async ({ uniform_id, qty, url_photo, notes }) => {
    try {
      await employeeService.updateReportDetail(editingDetail.uniform_report_detail_id, { qty, url_photo, notes });
      showToast('Data berhasil diperbarui');
      setEditingDetail(null);
      fetchAll();
    } catch (err) {
      showToast(err?.message || 'Gagal memperbarui data');
    }
  };

  const handleSaveNotes = async () => {
    setSavingNotes(true);
    try {
      await employeeService.updateReport(uniformReportId, { notes: notesText.trim() || null });
      setReport((prev) => ({ ...prev, notes: notesText.trim() || null }));
      setEditingNotes(false);
      showToast('Catatan berhasil disimpan');
    } catch (err) {
      showToast(err?.message || 'Gagal menyimpan catatan');
    } finally {
      setSavingNotes(false);
    }
  };


  if (loading) return <Loading />;
  if (!report) return <EmptyState icon="❌" title="Laporan tidak ditemukan" />;

  const { label } = parsePeriod(report.period);
  const isCurrent = report.period === CURRENT_PERIOD;
  const isDraft = report.report_status === 'draft';

  return (
    <>
      <div className="modal__body" style={{ paddingTop: 0 }}>
        {/* Report Summary */}
        <div className="detail-summary" style={{ marginBottom: 12 }}>
          <strong>{label}</strong>
          <span style={{ marginLeft: 10 }}><Badge status={report.report_status} /></span>
          <div style={{ marginTop: 4, color: '#6b7280', fontSize: 13 }}>
            Total: <strong>{details.reduce((sum, d) => sum + (Number(d.qty) || 0), 0)} pcs</strong>
          </div>

          {/* Report Notes */}
          <div style={{ marginTop: 10 }}>
            {!editingNotes ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 13, color: '#6b7280' }}>
                  Catatan:{' '}
                  {report.notes
                    ? <span style={{ color: '#374151' }}>{report.notes}</span>
                    : <em style={{ color: '#d1d5db' }}>Belum ada catatan</em>
                  }
                </span>
                {isDraft && (
                  <button
                    className="btn btn-sm btn-outline"
                    onClick={() => setEditingNotes(true)}
                  >
                    ✏️ Edit
                  </button>
                )}
              </div>
            ) : (
              <div>
                <textarea
                  value={notesText}
                  onChange={(e) => setNotesText(e.target.value)}
                  placeholder="Tambahkan catatan laporan..."
                  rows={2}
                  style={{ width: '100%', marginTop: 6, fontSize: 13, padding: '8px 10px', border: '1.5px solid #d1d5db', borderRadius: 8, resize: 'vertical', fontFamily: 'inherit' }}
                />
                <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
                  <button
                    className="btn btn-sm btn-outline"
                    onClick={() => { setNotesText(report.notes || ''); setEditingNotes(false); }}
                    disabled={savingNotes}
                  >
                    Batal
                  </button>
                  <button
                    className="btn btn-sm btn-primary"
                    onClick={handleSaveNotes}
                    disabled={savingNotes}
                  >
                    {savingNotes ? 'Menyimpan...' : 'Simpan'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Detail List */}
        {details.length === 0 ? (
          <EmptyState icon="📦" title="Belum ada detail" desc="Tambahkan item dari halaman utama laporan." />
        ) : (
          <>
            <div className="detail-list">
              {details.map((detail) => (
                <div key={detail.uniform_report_detail_id} className="detail-item">
                  {detail.url_photo ? (
                    <img
                      src={detail.url_photo}
                      alt={detail.uniform?.name}
                      className="detail-item__img detail-item__img--clickable"
                      onClick={() => setPreviewImg(detail.url_photo)}
                    />
                  ) : (
                    <div className="detail-item__img-placeholder" />
                  )}
                  <div className="detail-item__info">
                    <div className="detail-item__jenis">{detail.uniform?.name || '—'}</div>
                    <div className="detail-item__qty">{detail.qty} pcs</div>
                    {detail.notes && (
                      <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 2 }}>{detail.notes}</div>
                    )}
                  </div>
                  {isDraft && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, flexShrink: 0 }}>
                      <button
                        className="btn btn-sm btn-outline"
                        onClick={() => setEditingDetail(detail)}
                      >
                        Edit
                      </button>
                      <button
                        className="btn btn-sm btn-outline"
                        style={{ color: '#ef4444', borderColor: '#fca5a5' }}
                        onClick={() => handleDelete(detail.uniform_report_detail_id)}
                      >
                        Hapus
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Pagination */}
            {detailsMeta.last_page > 1 && (
              <div className="pagination">
                <button
                  className="btn btn-sm btn-outline"
                  disabled={detailsMeta.page <= 1}
                  onClick={() => setDetailsPage((p) => p - 1)}
                >
                  ‹ Sebelumnya
                </button>
                <span className="pagination__info">Hal {detailsMeta.page} / {detailsMeta.last_page}</span>
                <button
                  className="btn btn-sm btn-outline"
                  disabled={detailsMeta.page >= detailsMeta.last_page}
                  onClick={() => setDetailsPage((p) => p + 1)}
                >
                  Berikutnya ›
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {toast && <div className="toast">{toast}</div>}

      {previewImg && (
        <div className="img-lightbox" onClick={() => setPreviewImg(null)}>
          <button className="img-lightbox__close" onClick={() => setPreviewImg(null)}>✕</button>
          <img src={previewImg} alt="Preview" className="img-lightbox__img" onClick={(e) => e.stopPropagation()} />
        </div>
      )}

      {/* Edit Detail Modal */}
      {editingDetail && (
        <InputModal
          uniforms={uniforms}
          editMode={true}
          initialValues={{
            uniform_id: editingDetail.uniform_id,
            qty: editingDetail.qty,
            url_photo: editingDetail.url_photo || '',
            notes: editingDetail.notes || '',
          }}
          onSave={handleEditDetail}
          onClose={() => setEditingDetail(null)}
        />
      )}
    </>
  );
};

export default ReportDetailPage;
